import React from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Paper, Stack, Typography, alpha, useTheme } from "@mui/material";

import { ALPHA, FOCUS_RING } from "@shell/styles/designTokens";

export interface SelectCardProps {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export function SelectCard({
  label,
  description,
  icon,
  selected,
  onClick,
  disabled = false,
}: SelectCardProps): React.ReactElement {
  const theme = useTheme();

  return (
    <Paper
      component="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        textAlign: "left",
        p: 1.5,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 1,
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${alpha(theme.palette.divider, ALPHA.DIVIDER_ITEM)}`,
        bgcolor: selected
          ? alpha(theme.palette.primary.main, ALPHA.SURFACE_TINT)
          : "background.paper",
        opacity: disabled ? 0.5 : 1,
        transition: `border-color 150ms ease, background-color 150ms ease, box-shadow ${FOCUS_RING.TRANSITION}`,
        "&:hover": disabled
          ? {}
          : {
              bgcolor: selected
                ? alpha(theme.palette.primary.main, ALPHA.HOVER_ACTIVE)
                : alpha(theme.palette.primary.main, ALPHA.HOVER_DEFAULT),
            },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px ${theme.palette.background.paper}, 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px ${theme.palette.primary.main}`,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{ flex: 1 }}
      >
        {icon && (
          <Box
            sx={{
              color: selected ? "primary.main" : "text.secondary",
              mt: 0.25,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={selected ? 600 : 500} noWrap>
            {label}
          </Typography>
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {description}
            </Typography>
          )}
        </Box>
        <Box
          sx={{ color: selected ? "primary.main" : "text.disabled", mt: 0.25 }}
        >
          {selected ? (
            <CheckCircleIcon fontSize="small" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" />
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
